// AIAG-VDA Step Definitions for Compliance Tracking
// Reference: AIAG-VDA FMEA Handbook (2019)

export interface AIAGVDAStep {
    stepNumber: number;
    name: string;
    description: string;
    requiredFields: {
        fmea?: string[];
        pDiagram?: string[];
        controls?: string[];
        tests?: string[];
    };
    guidanceQuestions: string[];
}

export const AIAG_VDA_STEPS: AIAGVDAStep[] = [
    {
        stepNumber: 1,
        name: "Planning and Preparation",
        description: "Define project scope, team, and objectives",
        requiredFields: {
            fmea: ['projectName', 'revision', 'preparedBy'],
        },
        guidanceQuestions: [
            "Is the project scope clearly defined?",
            "Have all team members been identified?",
            "Are objectives and success criteria established?",
        ]
    },
    {
        stepNumber: 2,
        name: "Structure Analysis",
        description: "Define system structure and functional relationships",
        requiredFields: {
            pDiagram: ['systemName', 'systemFunction', 'inputSignals', 'expectedOutput'],
            fmea: ['function', 'itemFunction'],
        },
        guidanceQuestions: [
            "Are all system functions identified in P-Diagrams?",
            "Are functional requirements clearly stated?",
            "Is the structure hierarchy complete?",
        ]
    },
    {
        stepNumber: 3,
        name: "Function Analysis",
        description: "Analyze functions and their requirements",
        requiredFields: {
            pDiagram: ['goal', 'controlFactors', 'noiseFactors'],
            fmea: ['function', 'requirement'],
        },
        guidanceQuestions: [
            "Are all noise factors identified?",
            "Are control factors defined for each function?",
            "Do requirements have measurable criteria?",
        ]
    },
    {
        stepNumber: 4,
        name: "Failure Analysis",
        description: "Identify failure modes, effects, and causes",
        requiredFields: {
            fmea: ['failureMode', 'failureEffect', 'failureCause', 'severity'],
        },
        guidanceQuestions: [
            "Is every function analyzed for potential failures?",
            "Are failure effects traced to end user impact?",
            "Are root causes identified (not symptoms)?",
            "Is severity rated based on end-user impact?",
        ]
    },
    {
        stepNumber: 5,
        name: "Risk Analysis",
        description: "Assess and prioritize risks",
        requiredFields: {
            fmea: ['severity', 'occurrence', 'detection', 'RPN'],
        },
        guidanceQuestions: [
            "Are S-O-D ratings justified with evidence?",
            "Is Action Priority (AP) calculated per AIAG-VDA?",
            "Are high-risk items clearly flagged?",
            "Is detection based on current controls only?",
        ]
    },
    {
        stepNumber: 6,
        name: "Optimization",
        description: "Define and implement actions to reduce risk",
        requiredFields: {
            fmea: ['preventionControls', 'detectionControls', 'actionsTaken'],
            controls: ['type', 'name', 'effectiveness'],
            tests: ['testId', 'testName', 'criteria'],
        },
        guidanceQuestions: [
            "Are prevention controls identified for high-severity failures?",
            "Are detection controls in place for critical failures?",
            "Do all high-RPN items have action plans?",
            "Are controls validated through testing?",
        ]
    },
    {
        stepNumber: 7,
        name: "Results Documentation",
        description: "Document final results and communicate",
        requiredFields: {
            fmea: ['status', 'approvedBy', 'approvalDate'],
            tests: ['testResults', 'status'],
        },
        guidanceQuestions: [
            "Are all FMEA items reviewed and approved?",
            "Is test data documented and linked to failures?",
            "Have results been communicated to stakeholders?",
            "Is there a plan for ongoing monitoring?",
        ]
    }
];

// Helper function to get step by number
export const getAIAGVDAStep = (stepNumber: number): AIAGVDAStep | undefined => {
    return AIAG_VDA_STEPS.find(step => step.stepNumber === stepNumber);
};

// Helper to get all step names
export const getAllStepNames = (): string[] => {
    return AIAG_VDA_STEPS.map(step => `Step ${step.stepNumber}: ${step.name}`);
};
